import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { filter, switchMap, take } from 'rxjs';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { TopItemsService } from 'src/app/services/top-items/top-items.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { ChartComponent } from 'src/app/shared/components/chart/chart.component';
import { getServerName, getServerBackgroundColor, getServerBorderColor, getServerTextColor } from 'src/app/shared/constants/servers.constant';
import { ChartData } from 'src/app/shared/models/chart.models';
import { FlipLocationStatus } from 'src/app/shared/models/flipper.model';
import { FlipperStateService } from '../flipper-state.service';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';

@Component({
  selector: 'app-flipper-stats',
  imports: [CommonModule, ChartComponent, SmallNumberPipe, MatIcon, SectionComponent, MatButtonModule, MatTooltipModule],
  templateUrl: './flipper-stats.component.html',
  styleUrl: './flipper-stats.component.scss',
})
export class FlipperStatsComponent implements OnInit {
  constructor(
    public userSettingsService: UserSettingsService,
    public topItemsService: TopItemsService,
    public flipperStateService: FlipperStateService,
    public jsonReaderService: JsonReaderService,
    public loadingBarService: LoadingBarService
  ) {}

  ngOnInit() {
    this.jsonReaderService.ensureLoaded();

    if (!this.flipperStateService.chartData().length || !this.flipperStateService.flipStatus().length || !this.flipperStateService.flipHistoryStats().length) {
      this.updateStats();
    }
  }

  // MARK: updateStats
  updateStats() {
    this.loadFlipsCountersData();
    this.updateFlipsHistoryStats();
    this.updateFlipsLiveStats();
  }

  // MARK: loadFlipsCountersData
  loadFlipsCountersData(): void {
    this.flipperStateService.chartData.set([]);

    this.topItemsService
      .getFlipsCounters(7)
      .pipe(take(1))
      .subscribe((data) => {
        const serverGroups: Record<string, ChartData[]> = {};

        data.forEach((item) => {
          if (!serverGroups[item.server]) {
            serverGroups[item.server] = [];
          }
          serverGroups[item.server].push({
            date: item.dateTime.getTime(),
            value: Math.max(item.consumedNormalFlipsProfit + item.consumedUpgradeFlipsProfit, 0),
          });
        });

        const chartDataArray: { data: ChartData[]; name: string }[] = [];

        Object.entries(serverGroups).forEach(([server, items]) => {
          chartDataArray.push({ data: items, name: server });
        });

        this.flipperStateService.chartData.set(chartDataArray);
      });
  }

  //MARK: updateFlipsLiveStats
  updateFlipsLiveStats() {
    this.flipperStateService.flipStatus.set([]);
    this.flipperStateService.groupedFlipsLocationStatus.set({});

    this.topItemsService
      .getFlipsStatusLive()
      .pipe(take(1))
      .subscribe((flipperStats) => {
        // Handle flip status data
        this.flipperStateService.flipStatus.set(flipperStats.flipsStatus);
        this.flipperStateService.groupedFlipsLocationStatus.set(
          flipperStats.flipLocationsStatus.reduce((groups: { [key: string]: FlipLocationStatus[] }, item) => {
            const server = item.server;
            if (!groups[server]) {
              groups[server] = [];
            }
            groups[server].push(item);
            return groups;
          }, {})
        );
      });
  }

  // MARK: updateFlipsHistoryStats
  updateFlipsHistoryStats() {
    this.flipperStateService.flipHistoryStats.set([]);

    // Wait for jsonReaderService to be loaded before proceeding
    this.jsonReaderService.hasLoaded$
      .pipe(
        // Filter to only continue when hasLoaded is true
        filter((hasLoaded) => hasLoaded),
        // Take only the first true value
        take(1),
        // Then switch to the actual API call
        switchMap(() => this.topItemsService.getFlipsStatusHistory().pipe(take(1)))
      )
      .subscribe((flipsHistoryResponseArray) => {
        this.flipperStateService.flipHistoryStats.set(flipsHistoryResponseArray);
      });
  }

  // MARK: Server methods
  getServerName(id: string | number): string {
    id = Number(id) || id;
    return getServerName(id);
  }

  getServerBgColor(id: string | number): string {
    id = Number(id) || id;
    return getServerBackgroundColor(id);
  }

  getServerBorderColor(id: string | number): string {
    id = Number(id) || id;
    return getServerBorderColor(id);
  }

  getServerTextColor(id: string | number): string {
    id = Number(id) || id;
    return getServerTextColor(id);
  }
}
